import { TagDescription } from "@reduxjs/toolkit/query";
import Pusher from "pusher-js";
import { useEffect } from "react";

import { firebaseAuth } from "../configs/firebase";
import { useDispatch } from "../store";
import { EntityType, pomeriumApi } from "../store/pomeriumApi";

type Activity = {
  organizationId?: string;
  entityTypes: EntityType[];
};

type TagType =
  | "activityLog"
  | "changeset"
  | "cluster"
  | "featureFlag"
  | "keyPair"
  | "namespace"
  | "organization"
  | "policy"
  | "quota"
  | "route"
  | "settings";

function getTagsForActivity(activity: Activity): TagDescription<TagType>[] {
  const tags = new Set<TagDescription<TagType>>();
  tags.add("activityLog");
  tags.add("changeset");
  tags.add("quota");
  activity?.entityTypes?.forEach((entityType) => {
    switch (entityType) {
      case "domain":
        tags.add("cluster");
        break;
      case "feature_flag":
        tags.add("featureFlag");
        break;
      case "key_pair":
        tags.add("keyPair");
        break;
      case "namespace":
        tags.add("cluster");
        tags.add("namespace");
        break;
      case "organization":
        tags.add("organization");
        break;
      case "policy":
        tags.add("policy");
        tags.add("route");
        break;
      case "route":
        tags.add("policy");
        tags.add("route");
        break;
      case "settings":
        tags.add("settings");
        break;
    }
  });
  return Array.from(tags);
}

const headers: Record<string, string> = {};
const pusher =
  import.meta.env.VITE_PUSHER_APP_KEY && import.meta.env.VITE_PUSHER_CLUSTER
    ? new Pusher(import.meta.env.VITE_PUSHER_APP_KEY, {
        cluster: import.meta.env.VITE_PUSHER_CLUSTER,
        channelAuthorization: {
          transport: "ajax",
          endpoint: "/api/v0/notifications/authorize",
          headersProvider() {
            return headers;
          },
        },
      })
    : undefined;
let prevChangeKey = "";

export async function subscribe(
  orgId: string,
  abortSignal: AbortSignal,
  onActivity: (activity: Activity) => void,
): Promise<void> {
  await firebaseAuth.authStateReady();
  const token = await firebaseAuth.currentUser?.getIdToken();
  // if we've aborted this request, exit early so the latest org ids are used
  if (abortSignal.aborted) {
    return;
  }

  const changeKey = `${token}|${orgId}`;
  if (changeKey === prevChangeKey) {
    return;
  }
  prevChangeKey = changeKey;

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  if (!pusher) {
    console.log("pusher notifications disabled");
    return;
  }

  console.log("pusher notifications enabled, subscribing to org", orgId);

  // remove existing subscriptions
  pusher.allChannels().forEach((ch) => pusher.unsubscribe(ch.name));

  // add subscriptions

  pusher
    .subscribe("global")
    ?.bind("activity", (activity: any) => onActivity?.(activity as Activity));
  if (orgId) {
    pusher
      .subscribe(`private-organization-${orgId}`)
      ?.bind("activity", (activity: any) => {
        onActivity?.(activity as Activity);
      });
  }

  let interval: any;
  // eslint-disable-next-line
  interval = setInterval(async () => {
    if (abortSignal.aborted) {
      clearInterval(interval);
      return;
    }
    await firebaseAuth.authStateReady();
    const token = await firebaseAuth.currentUser?.getIdToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }, 30000);
}

function useServerNotifications(orgId: string | undefined): void {
  const dispatch = useDispatch();

  useEffect(() => {
    if (!orgId) {
      return;
    }

    const abort = new AbortController();
    subscribe(orgId, abort.signal, (activity) => {
      const tags = getTagsForActivity(activity);
      dispatch(pomeriumApi.util.invalidateTags(tags));
    });
    return () => {
      abort.abort();
    };
  }, [orgId]);
}

export default useServerNotifications;
